"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { VisibilityOption } from "@/components/organizations/VisibilityOption";
import { ArrowRightIcon, GlobeIcon, LockIcon } from "@/components/icons";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureUpgradePrompt, FeatureUpgradeBanner } from "@/components/billing/FeatureUpgradePrompt";
import {
  getServerLimitForSubscription,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "@/lib/billing/plans";

export default function NewOrganizationPage() {
  const router = useRouter();
  const createOrg = useMutationWithAuthRedirect(api.organizations.create);
  const user = useQuery(api.users.getCurrentUser);
  const ownedOrgs = useQuery(api.organizations.getOwned);

  // Feature gate: basic server creation (any paid subscription)
  const serverCreationGate = useFeatureAccess("server_creation");
  // Feature gate: creating servers beyond the plan's slot limit (Plus+)
  const beyondLimitGate = useFeatureAccess("server_creation_beyond_limit");

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [serverIp, setServerIp] = useState("");
  const [serverPort, setServerPort] = useState("");
  const [visibility, setVisibility] = useState<"public" | "page_only" | "private">("public");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const currentTier = (user?.subscriptionTier ?? "free") as SubscriptionTier;
  const currentStatus = (user?.subscriptionStatus ?? "none") as SubscriptionStatus;
  const serverLimit = getServerLimitForSubscription(currentTier, currentStatus);
  const activeOrgCount = ownedOrgs?.length ?? 0;
  const atLimit =
    user !== undefined &&
    user !== null &&
    ownedOrgs !== undefined &&
    serverLimit > 0 &&
    activeOrgCount >= serverLimit;

  // Still loading auth or Convex data
  if (
    serverCreationGate.isLoading ||
    user === undefined ||
    ownedOrgs === undefined
  ) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Not signed in
  if (user === null || !serverCreationGate.isSignedIn) {
    return (
      <div className="p-6 lg:p-8">
        <div className="mx-auto max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Sign in required</CardTitle>
              <CardDescription>
                Please sign in to create an organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link href="/login">
                <Button>Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // No active subscription — show upgrade prompt with trial countdown + Polar checkout
  if (!serverCreationGate.allowed) {
    return (
      <FeatureUpgradePrompt
        featureKey="server_creation"
        trialDaysLeft={serverCreationGate.trialDaysLeft}
      />
    );
  }

  // At server limit — show upgrade prompt for higher tier
  if (atLimit) {
    return (
      <FeatureUpgradePrompt
        featureKey="server_creation_beyond_limit"
        trialDaysLeft={beyondLimitGate.trialDaysLeft}
      />
    );
  }

  // Auto-generate slug from name
  const generateSlug = (orgName: string) => {
    return orgName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Organization name is required");
      return;
    }

    if (!slug.trim()) {
      setError("URL slug is required");
      return;
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      setError("Slug can only contain lowercase letters, numbers, and hyphens");
      return;
    }

    setLoading(true);

    try {
      await createOrg({
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
        serverIp: serverIp.trim() || undefined,
        serverPort: serverPort ? parseInt(serverPort, 10) : undefined,
        visibility,
      });
      router.push("/dashboard/my-orgs");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  // Approaching limit — show a soft-gate banner above the form
  const nearLimit = serverLimit > 0 && activeOrgCount >= serverLimit - 1;

  return (
    <div className="p-6 lg:p-8">
      <div className="mx-auto max-w-2xl">
        {nearLimit && !beyondLimitGate.allowed && (
          <div className="mb-4">
            <FeatureUpgradeBanner
              featureKey="server_creation_beyond_limit"
              trialDaysLeft={beyondLimitGate.trialDaysLeft}
            />
          </div>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Create Organization</CardTitle>
            <CardDescription>
              Set up a new gaming community for your server or group
              {serverLimit > 0 && (
                <span className="ml-1 text-xs text-muted">
                  ({activeOrgCount}/{serverLimit} slots used)
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {error && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {/* Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Organization Name *
                </label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="My Gaming Community"
                  disabled={loading}
                />
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <label htmlFor="slug" className="text-sm font-medium">
                  URL Slug *
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted">
                    blockynetworks.com/org/
                  </span>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase())}
                    placeholder="my-gaming-community"
                    disabled={loading}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted">
                  Only lowercase letters, numbers, and hyphens
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell people about your community..."
                  disabled={loading}
                  rows={3}
                  className="flex min-h-[80px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {/* Server Connection */}
              <div className="space-y-4">
                <h3 className="font-medium">Server Connection (Optional)</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="serverIp" className="text-sm font-medium">
                      Server IP
                    </label>
                    <Input
                      id="serverIp"
                      value={serverIp}
                      onChange={(e) => setServerIp(e.target.value)}
                      placeholder="play.example.com"
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="serverPort" className="text-sm font-medium">
                      Port
                    </label>
                    <Input
                      id="serverPort"
                      type="number"
                      value={serverPort}
                      onChange={(e) => setServerPort(e.target.value)}
                      placeholder="25565"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Visibility */}
              <div className="space-y-4">
                <h3 className="font-medium">Visibility</h3>
                <div className="grid gap-3">
                  <VisibilityOption
                    value="public"
                    selected={visibility}
                    onSelect={setVisibility}
                    icon={<GlobeIcon className="h-5 w-5" />}
                    title="Public"
                    description="Anyone can find and join from Server Finder"
                  />
                  <VisibilityOption
                    value="page_only"
                    selected={visibility}
                    onSelect={setVisibility}
                    icon={<GlobeIcon className="h-5 w-5 opacity-50" />}
                    title="Unlisted"
                    description="Only accessible via direct link, not in Server Finder"
                  />
                  <VisibilityOption
                    value="private"
                    selected={visibility}
                    onSelect={setVisibility}
                    icon={<LockIcon className="h-5 w-5" />}
                    title="Private"
                    description="Invite only, hidden from everyone"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Creating..." : "Create Organization"}
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
}
